import { Phone, Mail, MapPin, Linkedin, Facebook, Instagram } from 'lucide-react';
import { TID } from '@/constants/testIds';
import { BRAND } from '@/constants/content';

const QUICK_LINKS = [
    { label: 'About', id: 'about' },
    { label: 'Founders', id: 'founders' },
    { label: 'Services', id: 'services' },
    { label: 'Investment Solutions', id: 'investments' },
    { label: 'Process', id: 'process' },
    { label: 'FAQ', id: 'faq' },
    { label: 'Contact', id: 'contact' },
];

export default function Footer() {
    const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

    return (
        <footer className="bg-navy text-white relative overflow-hidden">
            <div className="absolute inset-0 grain opacity-20" />

            <div className="container-wl relative py-16 lg:py-20">
                <div className="grid lg:grid-cols-12 gap-12">
                    {/* Brand */}
                    <div className="lg:col-span-4">
                        <div className="flex items-center gap-3">
                            <img
                                src={BRAND.logoUrl}
                                alt="Waylthlane Financials"
                                className="h-14 w-14 object-contain bg-white p-1"
                            />
                            <div>
                                <p className="font-display-bold text-white text-[22px] leading-tight">
                                    Waylthlane
                                </p>
                                <p className="text-[11px] uppercase tracking-widest2 text-gold">
                                    Investment Reimagined
                                </p>
                            </div>
                        </div>

                        <p className="mt-6 text-[14px] text-white/65 leading-relaxed max-w-sm">
                            A Ghaziabad-based advisory firm helping families and professionals build
                            lasting wealth — guided by transparency, discipline and a long-term view.
                        </p>

                        <div className="mt-6 flex items-center gap-3">
                            <SocialIcon Icon={Linkedin} />
                            <SocialIcon Icon={Facebook} />
                            <SocialIcon Icon={Instagram} />
                        </div>
                    </div>

                    {/* Quick Links */}
                    <div className="lg:col-span-3">
                        <p className="text-[11px] uppercase tracking-widest2 text-gold font-semibold mb-5">
                            Quick Links
                        </p>
                        <ul className="space-y-3">
                            {QUICK_LINKS.map((l) => (
                                <li key={l.id}>
                                    <button
                                        onClick={() => go(l.id)}
                                        className="text-[14px] text-white/70 hover:text-gold transition-colors"
                                    >
                                        {l.label}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Contact */}
                    <div className="lg:col-span-5">
                        <p className="text-[11px] uppercase tracking-widest2 text-gold font-semibold mb-5">
                            Get in touch
                        </p>
                        <ul className="space-y-4">
                            <li className="flex items-start gap-3 text-[14px] text-white/80">
                                <MapPin className="w-4 h-4 mt-1 text-gold shrink-0" />
                                <span>{BRAND.address}</span>
                            </li>
                            <li className="flex items-center gap-3">
                                <Phone className="w-4 h-4 text-gold shrink-0" />
                                <div className="flex flex-wrap gap-x-4 text-[14px] text-white/80">
                                    <a data-testid={TID.footerPhone1} href={`tel:${BRAND.phones[0].replace(/\s/g, '')}`} className="hover:text-gold">
                                        {BRAND.phones[0]}
                                    </a>
                                    <a data-testid={TID.footerPhone2} href={`tel:${BRAND.phones[1].replace(/\s/g, '')}`} className="hover:text-gold">
                                        {BRAND.phones[1]}
                                    </a>
                                </div>
                            </li>
                            <li className="flex items-center gap-3 text-[14px] text-white/80">
                                <Mail className="w-4 h-4 text-gold shrink-0" />
                                <a data-testid={TID.footerEmail} href={`mailto:${BRAND.email}`} className="hover:text-gold break-all">
                                    {BRAND.email}
                                </a>
                            </li>
                        </ul>

                        <div className="mt-8 p-5 border border-white/10 bg-white/[0.03] rounded-xl">
                            <p className="text-[11px] uppercase tracking-widest2 text-gold font-semibold mb-2">
                                Compliance Notice
                            </p>
                            <p className="text-[12.5px] text-white/65 leading-relaxed">
                                Waylthlane Financials provides advisory and financial planning services.
                                Investments are subject to market risks; past performance is not
                                indicative of future returns.
                            </p>
                        </div>
                    </div>
                </div>

                <div className="mt-14 pt-7 border-t border-white/10 flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <p className="text-[12.5px] text-white/55">
                        © {new Date().getFullYear()} Waylthlane Financials. All rights reserved.
                    </p>
                    <div className="flex items-center gap-6 text-[12.5px] text-white/65">
                        <button data-testid={TID.footerPrivacy} className="hover:text-gold transition-colors">
                            Privacy Policy
                        </button>
                        <button data-testid={TID.footerTerms} className="hover:text-gold transition-colors">
                            Terms &amp; Conditions
                        </button>
                    </div>
                </div>
            </div>
        </footer>
    );
}

function SocialIcon({ Icon }) {
    return (
        <a
            href="#"
            className="w-9 h-9 flex items-center justify-center border border-white/15 text-white/70 hover:bg-gold hover:border-gold hover:text-navy transition-colors"
            onClick={(e) => e.preventDefault()}
            aria-label="Social link"
        >
            <Icon className="w-4 h-4" />
        </a>
    );
}
